import React, { Component } from 'react';
import HttpRunner from '../query';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';

class DocOrganizationEntity extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSample: perms.checkEndpoint(urlHandler.API_ENDPOINT_V2_ENRICH)
        }) 
    }

    render() {
        return (
            <div className="bucket" id="top">
                <div>
                    <h3 id="org" className="heading-1">Organization</h3>
                    <div className="description">
                        Organizational entities may be assigned an additional subtype(s) by the extraction model.
                    </div>

                    <div className="example-content">
                        <div className="line" id="sub_company">ORGANIZATION/COMPANY - <span className="example-content-nonitalic">a public or private business enterprise, e.g. "Walmart"</span></div>
                        <div className="line" id="sub_ticker">ORGANIZATION/COMPANY/TICKER - <span className="example-content-nonitalic">a stock ticker symbol, e.g. (NASDAQ:MSFT), (MSFT.OQ), (8306.T)</span></div>
                        <span className="line"><span className="example-content-nonitalic">
                            <div className="bullet-list">
                                <ul className="toc-level list-wrapper">
                                    <li className="list-item">NOTE: A ticker that is successfully disambiguated to a company 
                                        will be tagged as ORGANIZATION/COMPANY, so only tickers that are not disambiguated will be tagged 
                                        as ORGANIZATION/COMPANY/TICKER</li>
                                </ul>
                            </div>  
                        </span></span>                      
                        <div className="line" id="sub_criminal">ORGANIZATION/CRIMINAL - <span className="example-content-nonitalic">an organization with criminal activities, e.g. "ISIS"</span></div>
                        <div className="line" id="sub_educational">ORGANIZATION/EDUCATIONAL - <span className="example-content-nonitalic">an organization related to education, e.g. "Harvard"</span></div>
                        <div className="line" id="sub_fund">ORGANIZATION/FUND - <span className="example-content-nonitalic">an investment fund, e.g. "Voya Senior Income Fund"</span></div>
                        <div className="line" id="sub_government">ORGANIZATION/GOVERNMENT - <span className="example-content-nonitalic">a government organization, e.g. "FBI"</span></div>
                        <div className="line" id="sub_news">ORGANIZATION/NEWS - <span className="example-content-nonitalic">a news or media organization, e.g. "Washington Post"</span></div>
                        <div className="line" id="sub_performing">ORGANIZATION/PERFORMING - <span className="example-content-nonitalic">an organization related to performing arts, e.g. "Imagine Dragons"</span></div>
                        <div className="line" id="sub_socialmedia">ORGANIZATION/SOCIALMEDIA - <span className="example-content-nonitalic">a social media company, e.g. "Twitter"</span></div>
                        <div className="line" id="sub_sports">ORGANIZATION/SPORTS - <span className="example-content-nonitalic">an organization related to sports, e.g. "Boston Celtics"</span></div>
                        <div className="line" id="sub_trust">ORGANIZATION/TRUST - <span className="example-content-nonitalic">various forms of ownership trust, e.g. "Fannie Mae REMIC Trust 2011 50"</span></div>
                    </div>
                    <br/>

                    <div className="description">
                        The entity type of "ORGANIZATION" is assigned to organizations 
                        that are not assigned to one of the above organization subtypes.<br/>
                        <br/>
                        Organization white lists and black lists may be used to extend 
                        Finch for Text or to tune it for certain data domains. Please 
                        contact us for details.
                    </div>
                    
                    <div className="description">
                        An organization may be represented by the following attributes if it is accompanied by an acronym, 
                        abbreviation or stock symbol:
                    </div>

                    <table className="table-wrapper">
                        <thead className="table-header">
                        <tr>
                            <th>Attribute </th>
                            <th>Meaning </th>
                            <th>Type </th>
                            <th>Constraints </th>
                            <th>Notes </th>
                        </tr>
                        </thead>

                        <tbody className="table-body">
                        <tr>
                            <td className="attribute">longForm</td>
                            <td className="meaning">The long form of the organization acronym, which describes the short form.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">shortForm</td>
                            <td className="meaning">The short form of the organization acronym.</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div>
                    <h4 id="do" className="heading-3">Disambiguated Organization</h4>

                    <div className="description">
                        An organization that has been disambiguated will have a disambiguated object that contains the
                        top disambiguated result. We include the disambiguation confidence score, the id of the
                        entity, and the preferred label of the entity. The entity id can be used in a
                        GET {urlHandler.API_ENDPOINT_V2_ENTITIES}/{'{'}entity_id{'}'} call to retrieve more
                        information about the organization, as described above in the API section of
                        this document.
                    </div>

                    <h4 className="heading-4">Examples</h4>

                    <div className="example-content">Non Disambiguated Entities: "...The Alabama Entrepreneurial Research Network at the University of Alabama has received a $50,000 grant from the U.S. Department of Agriculture&apos;s Delta Regional Authority..." </div>
                    <HttpRunner example="org1" 
                        query="enrich"
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_ORG1) : null}
                    />
                    <br/>
                    <div className="example-content">Multiple entities: A handful of 
                    companies, including Purdue Pharma,  Allergan (AGN) and Johnson &amp; 
                    Johnson (JNJ), have been sued by the city of Seattle, WA related to 
                    OxyContin.</div>
                    <HttpRunner example="org2" 
                        query="enrich"
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_ORG2) : null}
                    />
                    <br/>
                </div>
                <div>
                    <h3 className="heading-2" id="stockticker">ORGANIZATION/COMPANY/TICKER</h3>
                    <div className="description">The extraction of a company stock ticker is modeled around common occurrences of stock 
                        market tickers where a general company symbol is accompanied by a well-known stock exchange code.</div>

                    <h5 className="heading-5">{"<exchange>:<symbol>"}</h5>
                    <div className="example-content">
                        <span className="line">NASDAQ:SYNT</span>
                        <span className="line">NYSE: M</span>
                        <span className="line">Nasdaq: AAPL</span>
                    </div>
                    <br/>

                    <div className="description">
                        An extracted company stock ticker will be represented by the following attributes:
                    </div>

                    <table className="table-wrapper">
                        <thead className="table-header">
                        <tr>
                            <th>Attribute </th>
                            <th>Meaning </th>
                            <th>Type </th>
                            <th>Constraints</th>
                            <th>Notes </th>
                        </tr>
                        </thead>
                        <tbody className="table-body">
                        <tr>
                            <td className="attribute">stockExchange</td>
                            <td className="meaning">The extracted stock exchange code</td>
                            <td className="type">String</td>
                            <td className="constraints">Mandatory</td>
                            <td className="notes">The exchange is case sensitive</td>
                        </tr>
                        <tr>
                            <td className="attribute">stockSymbol</td>
                            <td className="meaning">The extracted company symbol related to the corresponding stock exchange</td>
                            <td className="type">String</td>
                            <td className="constraints">Mandatory</td>
                            <td className="notes">The symbol is case sensitive</td>
                        </tr>
                        </tbody>
                    </table>

                    <h5 className="heading-3">Example</h5>
                    <div className="example-content">
                        VAALCO Energy, Inc. <b>(NYSE: EGY)</b> announced that it had received notification on November 30, 2016, from the 
                        New York Stock Exchange (“NYSE”) that the Company's market capitalization had fallen below the NYSE's continued 
                        listing standard. VAALCO is considered below criteria established by the NYSE because the Company’s average market 
                        capitalization fell below $50 million over a trailing consecutive 30 trading-day period and its last reported 
                        stockholders’ equity was less than $50 million.
                    </div>
                    <HttpRunner example="stockticker1" 
                        query="enrich"
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_ORG3) : null}
                    />
                </div>
            </div>
        );
    }
}

export default DocOrganizationEntity;